import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { studioHandoffUrl, toStudioHandoff } from '../../account/studio'
import { createSession } from '../../api/sessions'
import type { User } from '../../api/users'
import { currentAuth } from '../../state/session'

/**
 * Leave for Studio as a child. The platform mints the child a session of
 * their own at the parent's request, by the ownership rule, and the handoff
 * carries that session into Studio's storage — never the parent's.
 */
export function useOpenStudio(child: User, studioUrl: string) {
  const mutation = useMutation({
    mutationFn: async () =>
      toStudioHandoff(await createSession({ childId: child.id }, { auth: currentAuth() })),
    onSuccess: (handoff) => window.location.assign(studioHandoffUrl(studioUrl, handoff)),
    // Ours to debug; the parent gets the generic message on the card.
    onError: (cause) => console.error('[codeverse] studio handoff failed', cause),
  })

  // Back from Studio may restore the portal from the back/forward cache,
  // exactly as it was left: mid-handoff. The parent's session was never
  // touched, so start over, ready to open Studio again.
  const { reset } = mutation
  useEffect(() => {
    function onPageShow(event: PageTransitionEvent) {
      if (event.persisted) reset()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [reset])

  return mutation
}
